import React, { useEffect, createElement } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Star, ShieldCheck, Lock, CreditCard, Sparkles, Zap, FileText, ChevronRight } from 'lucide-react';
import { CountdownTimer } from '../components/CountdownTimer';
import { FAQItem } from '../components/FAQItem';
declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    _fbq?: any;
  }
}
const PRODUCT_IMG = 'https://cdn.magicpatterns.com/uploads/uTgUxDoTDMgo9wxAoa96aJ/MOCKOUP_THE_REVIVE_KIT.png';
const META_PIXEL_ID = '1167446325501718';
export function SalesPage() {
  // Meta Pixel: inject the base code once and track PageView
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.fbq) {
      window.fbq('track', 'PageView');
      return;
    }
    const n: any = window.fbq = function (...args: any[]) {
      n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
    };
    if (!window._fbq) window._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const target = document.head || document.documentElement;
    target.appendChild(script);
    window.fbq('init', META_PIXEL_ID);
    window.fbq('track', 'PageView');
  }, []);
  const trackCheckout = () => {
    window.fbq?.('track', 'InitiateCheckout');
  };
  const scrollToOffer = () => {
    document.getElementById('offer-cta')?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  };
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.6
    }
  };
  return <div className="min-h-screen bg-[#faf7f0] text-[#40493f] selection:bg-[#c39a3f]/30" data-id="element-21">
      {/* Meta Pixel noscript fallback */}
      <noscript data-id="element-22">
        <img height="1" width="1" style={{
        display: 'none'
      }} alt="" src={`https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`} data-id="element-23" />
      </noscript>

      {/* HEADER / HERO */}
      <header className="relative pt-8 pb-12 md:pt-10 md:pb-16 overflow-hidden bg-[#ecf1e7]" data-id="element-24">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_center,rgba(195,154,63,0.18),transparent_55%)] pointer-events-none" data-id="element-25" />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-26">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} className="inline-flex items-center space-x-1.5 bg-white border border-[#dfe6d6] rounded-full px-3 py-1 mb-3 shadow-sm" data-id="element-27">
            <Sparkles className="w-3 h-3 text-[#c39a3f]" data-id="element-28" />
            <span className="text-[10px] font-semibold tracking-wide text-[#23402f] uppercase" data-id="element-29">
              The Revive Kit
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-2xl md:text-4xl font-extrabold tracking-tight mb-2 leading-[1.2] text-[#23402f]" data-id="element-30">
            Your entire hormonal health. <br className="hidden md:block" data-id="element-31" />
            <span className="text-[#c39a3f]" data-id="element-32">Solved in 21 days.</span>
          </motion.h1>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="my-2 w-full max-w-[240px] sm:max-w-[280px] md:max-w-[320px] mx-auto relative" data-id="element-33">
            <div className="absolute inset-0 bg-[#c39a3f]/20 rounded-full blur-3xl opacity-50 pointer-events-none" data-id="element-34"></div>
            <img src={PRODUCT_IMG} alt="The Revive Kit Product Bundle" className="relative w-full object-contain drop-shadow-2xl mx-auto" data-id="element-35" />
          </motion.div>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="text-base md:text-lg mt-2 mb-6 max-w-2xl mx-auto text-[#40493f]" data-id="element-36">
            Open it, follow it, and feel like yourself again — everything your
            hormones need, ready to use today.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="flex flex-nowrap justify-center items-center gap-1.5 sm:gap-2 mb-5" data-id="element-37">
            <div className="flex items-center gap-1 text-[10px] sm:text-xs bg-white px-2 py-1.5 rounded-full border border-[#dfe6d6] whitespace-nowrap flex-shrink-0 shadow-sm" data-id="element-38">
              <FileText className="w-3 h-3 text-[#c39a3f] flex-shrink-0" data-id="element-39" />
              <span className="text-[#23402f] font-medium" data-id="element-40">100% PDF</span>
            </div>
            <div className="flex items-center gap-1 text-[10px] sm:text-xs bg-white px-2 py-1.5 rounded-full border border-[#dfe6d6] whitespace-nowrap flex-shrink-0 shadow-sm" data-id="element-41">
              <Zap className="w-3 h-3 text-[#c39a3f] flex-shrink-0" data-id="element-42" />
              <span className="text-[#23402f] font-medium" data-id="element-43">Instant Access</span>
            </div>
            <div className="flex items-center gap-1 text-[10px] sm:text-xs bg-white px-2 py-1.5 rounded-full border border-[#dfe6d6] whitespace-nowrap flex-shrink-0 shadow-sm" data-id="element-44">
              <Sparkles className="w-3 h-3 text-[#c39a3f] flex-shrink-0" data-id="element-45" />
              <span className="text-[#23402f] font-medium" data-id="element-46">
                Built for Real Women's Lives
              </span>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} data-id="element-47">
            <button onClick={scrollToOffer} className="group relative inline-flex items-center justify-center px-6 py-3.5 font-bold text-[#23402f] bg-[#c39a3f] rounded-full overflow-hidden transition-all hover:scale-105 hover:bg-[#b18c33] focus:outline-none focus:ring-2 focus:ring-[#c39a3f] focus:ring-offset-2 focus:ring-offset-[#ecf1e7] text-sm md:text-base w-full md:w-auto whitespace-nowrap shadow-[0_10px_30px_rgba(195,154,63,0.45)]" data-id="element-48">
              <span className="mr-2" data-id="element-49">🚀 I WANT THE REVIVE KIT NOW</span>
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform flex-shrink-0" data-id="element-50" />
            </button>

            <div className="mt-6 flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-sm text-[#5a6357]" data-id="element-51">
              <span className="flex items-center" data-id="element-52">
                🛡️ <span className="ml-1" data-id="element-53">Satisfied or Refunded in 7 Days</span>
              </span>
              <span className="flex items-center" data-id="element-54">
                🔒 <span className="ml-1" data-id="element-55">Secure Payment</span>
              </span>
              <span className="flex items-center" data-id="element-56">
                💳 <span className="ml-1" data-id="element-57">One-time Payment</span>
              </span>
            </div>
          </motion.div>
        </div>
      </header>

      {/* SOCIAL PROOF STATS */}
      <section className="border-y border-[#dfe6d6] bg-white py-8" data-id="element-58">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-59">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center divide-x divide-[#dfe6d6]" data-id="element-60">
            <div data-id="element-61">
              <div className="text-2xl md:text-3xl font-bold text-[#23402f] mb-1" data-id="element-62">
                +1,000
              </div>
              <div className="text-sm text-[#5a6357] uppercase tracking-wider" data-id="element-63">
                Women trust us
              </div>
            </div>
            <div data-id="element-64">
              <div className="text-2xl md:text-3xl font-bold text-[#23402f] mb-1" data-id="element-65">
                6
              </div>
              <div className="text-sm text-[#5a6357] uppercase tracking-wider" data-id="element-66">
                Volumes Included
              </div>
            </div>
            <div data-id="element-67">
              <div className="text-2xl md:text-3xl font-bold text-[#23402f] mb-1 flex items-center justify-center" data-id="element-68">
                4.9/5{' '}
                <Star className="w-5 h-5 text-[#c39a3f] fill-[#c39a3f] ml-1" data-id="element-69" />
              </div>
              <div className="text-sm text-[#5a6357] uppercase tracking-wider" data-id="element-70">
                Rating
              </div>
            </div>
            <div data-id="element-71">
              <div className="text-2xl md:text-3xl font-bold text-[#23402f] mb-1" data-id="element-72">
                🌍
              </div>
              <div className="text-sm text-[#5a6357] uppercase tracking-wider" data-id="element-73">
                Women Worldwide
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SHORT TESTIMONIALS */}
      <section className="py-12 bg-[#faf7f0]" data-id="element-74">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-75">
          <div className="grid md:grid-cols-3 gap-6" data-id="element-76">
            {[{
            init: 'LM',
            text: 'By day seven my energy was back. By day fourteen I felt like a completely different woman — I forgot what this felt like.',
            author: 'Linda M., Texas',
            role: 'Woman, 44 years old'
          }, {
            init: 'SR',
            text: 'I went from waking up exhausted every morning to actually looking forward to my day. The brain fog is completely gone.',
            author: 'Sandra R., Florida',
            role: 'Woman, 51 years old'
          }, {
            init: 'KP',
            text: 'My husband noticed the difference before I did. By week two he asked me what I had changed. The answer was The Revive Kit.',
            author: 'Karen P., California',
            role: 'Woman, 47 years old'
          }].map((t, i) => <motion.div key={i} {...fadeIn} className="glass-panel p-6 rounded-2xl relative" data-id="element-77">
                <div className="flex items-center space-x-1 mb-4" data-id="element-78">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-[#c39a3f] fill-[#c39a3f]" data-id="element-79" />)}
                </div>
                <p className="text-[#40493f] mb-6 italic" data-id="element-80">"{t.text}"</p>
                <div className="flex items-center space-x-3 mt-auto" data-id="element-81">
                  <div className="w-10 h-10 rounded-full bg-[#ecf1e7] flex items-center justify-center font-bold text-[#23402f]" data-id="element-82">
                    {t.init}
                  </div>
                  <div data-id="element-83">
                    <div className="font-semibold text-[#23402f] text-sm" data-id="element-84">
                      {t.author}
                    </div>
                    <div className="text-xs text-[#5a6357]" data-id="element-85">{t.role}</div>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* PAIN POINTS */}
      <section className="py-16 bg-[#ecf1e7] relative border-t border-[#dfe6d6]" data-id="element-86">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-87">
          <motion.div {...fadeIn} className="text-center mb-12" data-id="element-88">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-[#23402f]" data-id="element-89">
              If you are a woman between 35 and 55, you know this feeling all
              too well:
            </h2>
          </motion.div>

          <div className="space-y-4 mb-10" data-id="element-90">
            {['Waking up exhausted after 8 hours of sleep — every single morning.', 'Brain fog so thick you forget why you walked into a room.', 'Feeling bloated, inflamed and uncomfortable in your own body every single day.', 'Mood swings that make you feel like a stranger inside your own skin.', 'Doctors who run tests and tell you everything is "normal" — while you feel anything but normal.', 'Nights spent scrolling desperately through articles looking for answers that never come.', 'Watching the woman you used to be slowly disappear — and not knowing how to get her back.'].map((pain, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="flex items-start space-x-4 bg-white p-4 rounded-xl border border-[#dfe6d6]" data-id="element-91">
                <XCircle className="w-6 h-6 text-[#b5544b] flex-shrink-0 mt-0.5" data-id="element-92" />
                <span className="text-lg text-[#40493f]" data-id="element-93">{pain}</span>
              </motion.div>)}
          </div>

          <motion.div {...fadeIn} className="text-center" data-id="element-94">
            <h3 className="text-2xl md:text-3xl font-bold text-[#23402f]" data-id="element-95">
              How many mornings have you woken up feeling invisible — while your
              best years quietly slip by?
            </h3>
          </motion.div>
        </div>
      </section>

      {/* SOLUTION / PRODUCT INTRO */}
      <section className="py-16 bg-[#faf7f0] relative overflow-hidden" data-id="element-96">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#c39a3f]/5 rounded-full blur-3xl pointer-events-none" data-id="element-97"></div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-98">
          <motion.div {...fadeIn} data-id="element-99">
            <div className="inline-flex items-center space-x-2 mb-6" data-id="element-100">
              <Sparkles className="w-6 h-6 text-[#c39a3f]" data-id="element-101" />
              <span className="text-xl font-bold tracking-widest text-[#23402f] uppercase" data-id="element-102">
                The Revive Kit
              </span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold mb-8 leading-tight text-[#23402f]" data-id="element-103">
              THE REVIVE KIT™ <br data-id="element-104" />
              <span className="text-[#5a6357] text-3xl md:text-4xl" data-id="element-105">
                The Complete 21-Day Hormone & Energy Reset
              </span>
            </h2>

            <p className="text-xl text-[#40493f] max-w-3xl mx-auto mb-10 leading-relaxed" data-id="element-106">
              1 complete system built around 6 expert volumes. 300+ ready-to-use
              printable pages. 5 bonus guides included. Everything ready to use
              today.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center text-left" data-id="element-107">
            <motion.div {...fadeIn} className="space-y-6" data-id="element-108">
              <h3 className="text-2xl font-bold text-[#23402f] mb-8 flex items-center" data-id="element-109">
                <Sparkles className="w-6 h-6 text-[#c39a3f] mr-3" data-id="element-110" />6 EXPERT
                VOLUMES — ALL IN ONE SYSTEM
              </h3>

              {['Built on proven hormonal health principles', 'Clear daily actions for every stage of your reset', 'Designed for just 15 minutes per day', 'Works for busy women with real schedules', 'Covers nutrition, movement, sleep, stress, hormones and your full 21-day plan'].map((benefit, i) => <div key={i} className="flex items-start space-x-4" data-id="element-111">
                  <CheckCircle2 className="w-6 h-6 text-[#3e7a4e] flex-shrink-0 mt-0.5" data-id="element-112" />
                  <span className="text-lg text-[#40493f]" data-id="element-113">{benefit}</span>
                </div>)}
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} className="relative max-w-sm mx-auto" data-id="element-114">
              <img src={PRODUCT_IMG} alt="6 Volumes" className="w-full object-contain drop-shadow-2xl" data-id="element-115" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* THE VOLUMES */}
      <section className="py-16 bg-[#ecf1e7] border-y border-[#dfe6d6]" data-id="element-116">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-117">
          <motion.div {...fadeIn} className="text-center mb-10" data-id="element-118">
            <h2 className="text-3xl md:text-5xl font-bold mb-4 text-[#23402f]" data-id="element-119">
              What you get in <br data-id="element-120" />
              <span className="text-[#c39a3f]" data-id="element-121">THE REVIVE KIT™</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-122">
            {[{
            vol: '1',
            title: 'THE ENERGY BLUEPRINT',
            desc: 'Chronic fatigue is not a personality trait. This volume shows you exactly why your energy crashed — and the precise steps to get it back for good.',
            color: '#23402f'
          }, {
            vol: '2',
            title: 'NOURISH & BALANCE',
            desc: 'Everything you eat is either feeding your hormones or fighting them. This volume rebuilds your nutritional foundation from the ground up.',
            color: '#4b7355'
          }, {
            vol: '3',
            title: 'THE HORMONE-FRIENDLY KITCHEN',
            desc: '80+ anti-inflammatory recipes designed to lower cortisol, eliminate bloating and fuel real sustainable energy — meal by meal, day by day.',
            color: '#c39a3f'
          }, {
            vol: '4',
            title: 'MOVE & RECHARGE',
            desc: 'The wrong exercise makes hormonal imbalance worse. This volume gives you the exact movement your body needs — in just 10–20 minutes a day.',
            color: '#4b7355'
          }, {
            vol: '5',
            title: 'SLEEP & STRESS RESET',
            desc: 'You cannot heal a body that never truly rests. This volume rebuilds your sleep quality, lowers your cortisol and resets your nervous system.',
            color: '#c39a3f'
          }, {
            vol: '6',
            title: 'THE 21-DAY REVIVE PLAN',
            desc: 'Everything comes together here. Your complete day-by-day plan with daily menus, morning and evening routines, and a full progress tracker.',
            color: '#23402f'
          }].map((v, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="glass-panel rounded-2xl p-8 border-t-4 hover:-translate-y-1 transition-transform duration-300" style={{
            borderTopColor: v.color
          }} data-id="element-123">
                <div className="text-sm font-bold tracking-wider mb-2 uppercase text-[#c39a3f]" data-id="element-124">
                  Volume {v.vol}
                </div>
                <h3 className="text-xl font-bold text-[#23402f] mb-4" data-id="element-125">
                  {v.title}
                </h3>
                <p className="text-[#5a6357] leading-relaxed" data-id="element-126">{v.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* BONUSES */}
      <section className="py-16 bg-[#faf7f0]" data-id="element-127">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-128">
          <motion.div {...fadeIn} className="text-center mb-10" data-id="element-129">
            <div className="inline-block bg-[#c39a3f]/10 border border-[#c39a3f]/30 text-[#a9832f] px-4 py-1.5 rounded-full text-sm font-bold tracking-wider mb-4 uppercase" data-id="element-130">
              Free Bonuses
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-6 text-[#23402f]" data-id="element-131">
              5 Exclusive Bonus Guides Included
            </h2>
            <p className="text-xl text-[#5a6357]" data-id="element-132">
              On top of the 6 volumes, you also receive these 5 bonus guides at
              no extra cost:
            </p>
          </motion.div>

          <div className="space-y-4" data-id="element-133">
            {[{
            num: '01',
            title: '21-Day Hormone-Friendly Meal Plan',
            desc: 'Your complete 3-week menu — already planned and done for you. Open it, follow the meals and feed your hormones every single day.'
          }, {
            num: '02',
            title: 'Done-For-You Grocery Shopping Guide',
            desc: 'Walk into any store with total clarity. Every ingredient organized by category so you never waste time or money again.'
          }, {
            num: '03',
            title: '30+ Energy Smoothie Recipes',
            desc: 'Fast, hormone-supportive smoothies for busy mornings, afternoon energy crashes and real life schedules.'
          }, {
            num: '04',
            title: 'Habit & Wellness Tracker',
            desc: 'Track your water, sleep, energy and mood every day. What gets measured gets improved. See your results from day one to twenty-one.'
          }, {
            num: '05',
            title: 'Morning & Evening Routine Guide',
            desc: 'Simple daily rituals that anchor your hormones, calm your nervous system and make your results last long after the 21 days.'
          }].map((b, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="flex flex-col md:flex-row md:items-center justify-between bg-white p-6 rounded-2xl border border-[#dfe6d6] shadow-[0_6px_18px_rgba(35,64,47,0.05)]" data-id="element-134">
                <div className="flex items-start md:items-center space-x-6 mb-4 md:mb-0" data-id="element-135">
                  <div className="text-3xl font-black text-[#dfe6d6]" data-id="element-136">
                    {b.num}
                  </div>
                  <div data-id="element-137">
                    <div className="text-xs font-bold uppercase tracking-wider text-[#c39a3f] mb-1" data-id="element-138">
                      Bonus #{b.num}
                    </div>
                    <h4 className="text-xl font-bold text-[#23402f] mb-1" data-id="element-139">
                      {b.title}
                    </h4>
                    <p className="text-[#5a6357]" data-id="element-140">{b.desc}</p>
                  </div>
                </div>
                <div className="flex-shrink-0 md:text-right pl-14 md:pl-0" data-id="element-141">
                  <div className="text-[#9aa393] line-through text-sm" data-id="element-142">
                    Value $17
                  </div>
                  <div className="font-black text-lg uppercase tracking-wider text-[#c39a3f]" data-id="element-143">
                    FREE
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* OFFER / PRICING */}
      <section id="offer" className="py-16 bg-[#ecf1e7] relative overflow-hidden" data-id="element-144">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(195,154,63,0.12),transparent_70%)] pointer-events-none" data-id="element-145" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-146">
          <motion.div {...fadeIn} className="text-center mb-12" data-id="element-147">
            <h2 className="text-3xl md:text-5xl font-bold mb-6 text-[#23402f]" data-id="element-148">
              ⏰ Launch price ends soon
            </h2>
            <CountdownTimer data-id="element-149" />
            <p className="text-[#5a6357] mt-6 text-lg" data-id="element-150">
              When the timer ends, the price returns to $97. Lock in the launch
              price today.
            </p>

            <div className="mt-8 max-w-md mx-auto" data-id="element-151">
              <div className="flex justify-between text-sm text-[#5a6357] mb-2 font-medium" data-id="element-152">
                <span data-id="element-153">Launch-price spots</span>
                <span className="text-[#a9832f] font-bold" data-id="element-154">88% claimed</span>
              </div>
              <div className="h-2 bg-white border border-[#dfe6d6] rounded-full overflow-hidden" data-id="element-155">
                <div className="h-full bg-[#c39a3f] w-[88%] rounded-full" data-id="element-156"></div>
              </div>
            </div>
          </motion.div>

          <motion.div id="pricing-card" initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="scroll-mt-24 bg-white border-2 border-[#c39a3f] rounded-3xl p-8 md:p-12 shadow-[0_20px_50px_rgba(195,154,63,0.18)] relative" data-id="element-157">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#c39a3f] text-[#23402f] px-6 py-2 rounded-full font-bold flex items-center space-x-2 shadow-lg" data-id="element-158">
              <Star className="w-4 h-4 fill-[#23402f]" data-id="element-159" />
              <span data-id="element-160">Most Popular Choice</span>
            </div>

            <div className="text-center mb-10 mt-4" data-id="element-161">
              <div className="flex items-center justify-center gap-3 mb-2" data-id="element-162">
                <img src={PRODUCT_IMG} alt="The Revive Kit" className="h-14 md:h-16 w-auto object-contain flex-shrink-0" data-id="element-163" />
                <h3 className="text-xl md:text-2xl font-bold text-[#23402f]" data-id="element-164">
                  The Revive Kit™
                </h3>
              </div>
              <p className="text-xl text-[#4b7355] font-medium mb-4" data-id="element-165">
                The Complete 21-Day Hormone & Energy Reset
              </p>
              <p className="text-[#5a6357]" data-id="element-166">
                For women 35–55 · Instant PDF · Print at home
              </p>
            </div>

            <div className="space-y-4 mb-10" data-id="element-167">
              {['6 Expert Volumes (300+ printable pages)', 'Full done-for-you 21-day system', '5 Bonus Guides Included (worth $85+)', 'Lifetime Access — pay once, keep forever', 'Print as many times as you need — no limits'].map((feature, i) => <div key={i} className="flex items-start space-x-3" data-id="element-168">
                  <CheckCircle2 className="w-6 h-6 text-[#3e7a4e] flex-shrink-0" data-id="element-169" />
                  <span className="text-lg text-[#40493f]" data-id="element-170">{feature}</span>
                </div>)}
            </div>

            <div className="text-center mb-8 p-6 bg-[#faf7f0] rounded-2xl border border-[#dfe6d6]" data-id="element-171">
              <div className="flex items-center justify-center space-x-4 mb-2" data-id="element-172">
                <span className="text-3xl text-[#9aa393] line-through font-medium" data-id="element-173">
                  $97
                </span>
                <span className="bg-[#c39a3f] text-[#23402f] px-3 py-1 rounded-full text-sm font-bold" data-id="element-174">
                  🔥 80% OFF
                </span>
              </div>
              <div className="text-6xl font-black text-[#c39a3f] mb-2" data-id="element-175">
                $19{' '}
                <span className="text-2xl text-[#9aa393] font-medium" data-id="element-176">USD</span>
              </div>
              <p className="text-[#3e7a4e] font-semibold" data-id="element-177">You save $78 today</p>
              <p className="text-sm text-[#5a6357] mt-2 flex items-center justify-center" data-id="element-178">
                <CreditCard className="w-4 h-4 mr-1" data-id="element-179" /> One-time · Pay in your
                local currency
              </p>
            </div>

            <a id="offer-cta" href="https://the-revive-kit.impultienda.ar/checkout" onClick={trackCheckout} className="scroll-mt-24 w-full group relative inline-flex items-center justify-center px-8 py-5 font-bold text-[#23402f] bg-[#c39a3f] rounded-2xl overflow-hidden transition-all hover:scale-[1.02] hover:bg-[#b18c33] focus:outline-none focus:ring-2 focus:ring-[#c39a3f] focus:ring-offset-2 focus:ring-offset-white text-xl shadow-[0_12px_34px_rgba(195,154,63,0.4)]" data-id="element-180">
              <span className="mr-2" data-id="element-181">🚀 YES, I WANT MY 21-DAY RESET NOW</span>
            </a>

            <div className="mt-6 flex flex-col items-center space-y-3 text-sm text-[#5a6357]" data-id="element-182">
              <div className="flex items-center space-x-4" data-id="element-183">
                <span className="flex items-center" data-id="element-184">
                  <Zap className="w-4 h-4 mr-1 text-[#c39a3f]" data-id="element-185" /> Instant access
                  in 5 min
                </span>
                <span className="flex items-center" data-id="element-186">
                  <Lock className="w-4 h-4 mr-1 text-[#c39a3f]" data-id="element-187" /> Secure SSL
                  checkout
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FULL TESTIMONIALS */}
      <section className="py-16 bg-[#faf7f0] border-t border-[#dfe6d6]" data-id="element-188">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-189">
          <motion.div {...fadeIn} className="text-center mb-10" data-id="element-190">
            <h2 className="text-3xl font-bold mb-4 uppercase tracking-wider text-[#5a6357] text-sm" data-id="element-191">
              What women who already have The Revive Kit™ say:
            </h2>
          </motion.div>

          <div className="space-y-6" data-id="element-192">
            {[{
            init: 'LM',
            name: 'Linda M.',
            loc: 'Texas',
            role: 'Woman, 44',
            text: 'By day seven my energy was back. By day fourteen I felt like a completely different woman. I forgot what it felt like to wake up and actually want to start the day.'
          }, {
            init: 'SR',
            name: 'Sandra R.',
            loc: 'Florida',
            role: 'Woman, 51',
            text: 'The Hormone-Friendly Kitchen volume alone was worth every penny. I lost 6 pounds of bloating in 21 days without starving myself. My brain fog is completely gone.'
          }, {
            init: 'KP',
            name: 'Karen P.',
            loc: 'California',
            role: 'Woman, 47',
            text: 'I was deeply skeptical. By day five my energy was noticeably back. By day fourteen my husband noticed the difference before I did. I have recommended this to every woman I know over 40.'
          }, {
            init: 'MP',
            name: 'Michelle P.',
            loc: 'New York',
            role: 'Woman, 49',
            text: 'I have spent three years searching for something that actually works. This is the first thing that made me feel human again. Week three was a turning point I will never forget.'
          }, {
            init: 'JT',
            name: 'Jennifer T.',
            loc: 'Ohio',
            role: 'Woman, 52',
            text: 'My doctor kept telling me it was just aging. The Revive Kit proved her wrong in 21 days. I feel better now than I did at 38.'
          }, {
            init: 'RB',
            name: 'Rebecca B.',
            loc: 'Toronto',
            role: 'Woman, 45',
            text: 'Everything is organized and ready to use. I always know exactly what to do next. No guessing. No research. No stress. Just open it and follow it.'
          }].map((t, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="bg-white p-6 md:p-8 rounded-2xl border border-[#dfe6d6] shadow-[0_6px_18px_rgba(35,64,47,0.05)] flex flex-col md:flex-row gap-6" data-id="element-193">
                <div className="flex-shrink-0 flex items-center md:flex-col md:items-start gap-4 md:w-64" data-id="element-194">
                  <div className="w-12 h-12 rounded-full bg-[#ecf1e7] flex items-center justify-center font-bold text-[#23402f] text-lg" data-id="element-195">
                    {t.init}
                  </div>
                  <div data-id="element-196">
                    <div className="font-bold text-[#23402f]" data-id="element-197">{t.name}</div>
                    <div className="text-sm text-[#5a6357] flex items-center mt-1" data-id="element-198">
                      📍 {t.loc}
                    </div>
                    <div className="text-xs text-[#5a6357] mt-1" data-id="element-199">{t.role}</div>
                  </div>
                </div>
                <div className="flex-1" data-id="element-200">
                  <div className="flex items-center space-x-1 mb-3" data-id="element-201">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-[#c39a3f] fill-[#c39a3f]" data-id="element-202" />)}
                  </div>
                  <p className="text-lg text-[#40493f] leading-relaxed" data-id="element-203">
                    "{t.text}"
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* GUARANTEE */}
      <section className="py-16 bg-[#ecf1e7]" data-id="element-204">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-205">
          <motion.div {...fadeIn} className="bg-white border border-[#dfe6d6] p-8 md:p-16 rounded-3xl text-center relative overflow-hidden shadow-[0_16px_40px_rgba(35,64,47,0.08)]" data-id="element-206">
            <ShieldCheck className="w-24 h-24 text-[#c39a3f]/15 absolute -top-4 -right-4" data-id="element-207" />

            <ShieldCheck className="w-16 h-16 text-[#c39a3f] mx-auto mb-6 relative z-10" data-id="element-208" />
            <h2 className="text-3xl md:text-4xl font-bold text-[#23402f] mb-8 relative z-10" data-id="element-209">
              🛡️ TOTAL GUARANTEE — 7 DAYS
            </h2>

            <div className="space-y-6 text-lg text-[#40493f] relative z-10 max-w-2xl mx-auto" data-id="element-210">
              <p data-id="element-211">
                Download everything. Print the guides. Follow the first week of
                the plan.
              </p>
              <p className="font-semibold text-[#23402f]" data-id="element-212">
                If for any reason The Revive Kit™ does not improve your energy,
                reduce your bloating or help you start feeling like yourself
                again — we refund you 100%. No questions asked. No forms to
                fill. No awkward emails.
              </p>
              <p className="text-[#5a6357] text-base" data-id="element-213">
                The confidence we have in this system is exactly what lets us
                offer you this.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-[#faf7f0] border-t border-[#dfe6d6]" data-id="element-214">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-215">
          <motion.div {...fadeIn} className="text-center mb-10" data-id="element-216">
            <h2 className="text-3xl md:text-4xl font-bold text-[#23402f]" data-id="element-217">
              ❓ FREQUENTLY ASKED QUESTIONS
            </h2>
          </motion.div>

          <div className="space-y-2" data-id="element-218">
            {[{
            q: '1 — Is this a physical product?',
            a: 'No. The Revive Kit™ is a 100% digital product. After your purchase you receive instant access to all 6 volumes and 5 bonus guides in PDF format. You can read them on any device or print them at home whenever you want.'
          }, {
            q: '2 — What age or stage is this designed for?',
            a: 'This system is specifically designed for women between 35 and 55 experiencing hormonal changes, fatigue, brain fog, bloating or mood swings. Whether you are in perimenopause, menopause or simply feeling off — this kit was built for you.'
          }, {
            q: '3 — How do I receive access after purchase?',
            a: 'Immediately after completing your purchase you will receive an email with a download link. Your full kit will be ready to access in under 5 minutes.'
          }, {
            q: '4 — Do I need any health or nutrition experience?',
            a: 'None at all. Every volume is written in plain, clear language. No medical background required. Everything is explained step by step so you can open it today and start immediately.'
          }, {
            q: '5 — Are all 5 bonuses really included?',
            a: 'Yes — completely. All 5 bonus guides are included at no extra cost with every purchase. The Meal Plan, Grocery Guide, Smoothie Recipes, Wellness Tracker and Routine Guide are all yours the moment you complete your order.'
          }].map((faq, i) => <FAQItem key={i} question={faq.q} answer={faq.a} data-id="element-219" />)}
          </div>
        </div>
      </section>

      {/* FINAL CTA & FOOTER */}
      <footer className="bg-[#23402f] pt-16 pb-12 relative text-[#e9efe4]" data-id="element-220">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-221">
          <div className="inline-flex items-center space-x-2 mb-8" data-id="element-222">
            <Sparkles className="w-6 h-6 text-[#c39a3f]" data-id="element-223" />
            <span className="text-xl font-bold tracking-widest text-white uppercase" data-id="element-224">
              The Revive Kit
            </span>
          </div>

          <p className="text-2xl md:text-3xl font-medium text-white mb-8 leading-relaxed" data-id="element-225">
            Tonight, you could spend another evening feeling exhausted, bloated
            and disconnected from the woman you used to be — while your best
            years quietly slip by.
          </p>
          <p className="text-xl text-[#c3d0ba] mb-12" data-id="element-226">
            Or you could spend 15 minutes starting the exact reset that brings
            your energy, your clarity and yourself back —{' '}
            <span className="text-[#e2c069] font-semibold" data-id="element-227">
              starting right now.
            </span>
          </p>

          <div className="mb-12" data-id="element-228">
            <CountdownTimer data-id="element-229" />
          </div>

          <a href="https://the-revive-kit.impultienda.ar/checkout" onClick={trackCheckout} className="group relative inline-flex items-center justify-center px-8 py-5 font-bold text-[#23402f] bg-[#c39a3f] rounded-full overflow-hidden transition-all hover:scale-105 hover:bg-[#d3ac52] focus:outline-none focus:ring-2 focus:ring-[#c39a3f] focus:ring-offset-2 focus:ring-offset-[#23402f] text-xl w-full md:w-auto shadow-[0_12px_34px_rgba(195,154,63,0.4)] mb-8" data-id="element-230">
            <span className="mr-2" data-id="element-231">⚡ THE COMPLETE 21-DAY RESET — $19</span>
            <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-232" />
          </a>

          <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-3 text-sm text-[#c3d0ba] mb-24" data-id="element-233">
            <span className="flex items-center" data-id="element-234">
              <Lock className="w-4 h-4 mr-1" data-id="element-235" /> Secure Payment
            </span>
            <span className="flex items-center" data-id="element-236">
              <Zap className="w-4 h-4 mr-1" data-id="element-237" /> Access in 5 minutes
            </span>
            <span className="flex items-center" data-id="element-238">
              <ShieldCheck className="w-4 h-4 mr-1" data-id="element-239" /> 7-Day Guarantee
            </span>
            <span className="flex items-center" data-id="element-240">
              <CreditCard className="w-4 h-4 mr-1" data-id="element-241" /> Card | PayPal | Stripe
            </span>
          </div>

          <div className="border-t border-[#3a5544] pt-12 text-left md:text-center" data-id="element-242">
            <div className="inline-flex items-center space-x-2 mb-6 opacity-70" data-id="element-243">
              <Sparkles className="w-5 h-5 text-[#c39a3f]" data-id="element-244" />
              <span className="text-lg font-bold tracking-widest uppercase text-[#c3d0ba]" data-id="element-245">
                The Revive Kit
              </span>
            </div>
            <p className="text-[#a8b8a0] text-sm max-w-2xl mx-auto mb-8 leading-relaxed" data-id="element-246">
              The "done-for-you" hormone and energy reset for women who are done
              feeling exhausted — and ready to feel like themselves again.
              <br data-id="element-247" />
              <br data-id="element-248" />
              Because feeling healthy, energized and balanced after 35 should
              not cost you years of trial and error.
            </p>
            <div className="text-[#8fa086] text-xs flex flex-col md:flex-row items-center justify-center space-y-2 md:space-y-0 md:space-x-4" data-id="element-249">
              <span data-id="element-250">© 2026 EurekaLabs.store™ — All Rights Reserved.</span>
              <span className="hidden md:inline" data-id="element-251">•</span>
              <span data-id="element-252">🌐 www.eurekalabs.store</span>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}